// components
import {
  IconButton,
  StyledEngineProvider,
  Typography,
  Stack
} from '@mui/material';
import Alert, { AlertProps } from '@mui/material/Alert';
import { SnackbarProps } from '@mui/material/Snackbar';
import { StyledTypographyBodyBold } from './Typography/Typography.styles';
import { ThemeProvider } from '@mui/material/styles'; // import types
import { ShowSnackbarProps } from './Snackbar.types';
// utils

import { download } from 'utils/download';
import theme from 'config/theme';
import i18n from 'i18next';
import { createRoot } from 'react-dom/client';
import { Icon } from '@sdb/planning-ui';

// styles
import { StyledSnackbar } from './Snackbar.styles';

export default function showSnackbar(
  props: SnackbarProps & AlertProps & ShowSnackbarProps
) {
  const {
    message,
    messageCode,
    messageCodeProps,
    downloadUrl,
    filename,
    severity = 'info',
    anchorOrigin,
    autoHideDuration = 6000,
    variant = 'standard',
    color,
    icon,
    hideCloseButton
  } = props;

  const container = document.createElement('div');

  document.body.appendChild(container);

  const root = createRoot(container);

  const onClose = () => {
    renderSnackBar(false);
    root.unmount();
  };

  const getMessageCodeProps = () => {
    const translatedProps = {};
    const mcp = messageCodeProps || {};
    Object.keys(mcp).forEach((key) => {
      const value = mcp[key] || '';
      translatedProps[key] = i18n.t(value);
    });
    return translatedProps;
  };
  const getMessage = () => {
    return messageCode
      ? i18n.t(messageCode, getMessageCodeProps())
      : typeof message === 'string'
      ? message
      : JSON.stringify(message);
  };

  const renderSnackBar = (isOpen: boolean) => {
    root.render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <StyledSnackbar
            open={isOpen}
            autoHideDuration={autoHideDuration}
            anchorOrigin={
              anchorOrigin || { vertical: 'top', horizontal: 'center' }
            }
            TransitionProps={{
              onExited: () => document.body.removeChild(container)
            }}
            onClose={onClose}
          >
            <Alert
              severity={severity}
              onClose={hideCloseButton ? undefined : onClose}
              variant={variant}
              color={color}
              icon={icon}
              iconMapping={{
                success: <Icon type="circle-check" />,
                info: <Icon type="circle-info" />,
                warning: <Icon type="circle-info" />,
                error: <Icon type="triangle-exclamation" />
              }}
              action={
                downloadUrl &&
                filename && (
                  <IconButton
                    onClick={() => download(downloadUrl, filename)}
                    color="primary"
                    size="small"
                  >
                    <Icon type="download" fontSize="small" />
                  </IconButton>
                )
              }
            >
              <Stack>
                <StyledTypographyBodyBold variant="body1">
                  {i18n.t(`snackbar.severity.${severity?.toUpperCase()}`)}
                </StyledTypographyBodyBold>
                <Typography variant="body2">{getMessage()}</Typography>
              </Stack>
            </Alert>
          </StyledSnackbar>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  };

  renderSnackBar(true);
}

export const showSuccessSnackbar = () =>
  showSnackbar({
    severity: 'success',
    message: i18n.t('changesSaved')
  });

export const showErrorSnackbar = (error?: string) =>
  showSnackbar({
    severity: 'error',
    message: error ?? i18n.t('somethingWentWrong')
  });
